import { CallToActionButton, FlatButton, LinkButton } from '@finn-no/troika-react-buttons';
import { type ReactNode, useState } from 'react';
import type { Month } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';

interface Props {
    title: string;
    firstYear: number;
    firstMonth: number;
    size: number;
    initialValues: Month[];
    toggleMonth: (month: Month) => void;
    resetMonths: () => void;
    close: () => void;
}

type CalendarMonth = Month & { selected?: boolean };

interface CalendarYear {
    [key: number]: CalendarMonth;
}
interface Calendar {
    [key: number]: CalendarYear;
}

const isSelected = (selectedMonths: Month[], month: number, year: number): boolean =>
    selectedMonths.findIndex((e: Month) => e.month === month && e.year === year) >= 0;

const yearObject = (year: number, startMonth: number, size: number, initialValues: Month[]): CalendarYear => {
    const calendarYear: CalendarYear = {};

    for (let index = 0; index < size; index++) {
        const monthNumber = index + startMonth + 1;

        calendarYear[monthNumber] = {
            year,
            month: monthNumber,
            selected: isSelected(initialValues, monthNumber, year),
        };
    }

    return calendarYear;
};

export const generateCalendar = (firstYear: number, firstMonth: number, size: number, initialValues: Month[]): Calendar => {
    const calendar: Calendar = {};
    let monthsLeft = size;
    let currentYear = firstYear;
    let currentMonth = firstMonth;

    while (monthsLeft > 0) {
        const monthsToAdd = Math.min(12, monthsLeft) - currentMonth;
        calendar[currentYear] = yearObject(currentYear++, currentMonth, monthsToAdd, initialValues);
        monthsLeft -= monthsToAdd;
        currentMonth = 0;
    }
    return calendar;
};

export const MonthCalendar = ({ title, firstYear, firstMonth, size, initialValues, toggleMonth, resetMonths, close }: Props) => {
    const [calendar, setCalendar] = useState(generateCalendar(firstYear, firstMonth, size, initialValues));

    const monthNames = displayValues.months as Record<string, string>[];

    const monthClicked = (month: CalendarMonth): void => {
        toggleMonth(month);
        calendar[month.year][month.month].selected = !month.selected;
        setCalendar(calendar);
    };

    const reset = (): void => {
        setCalendar(generateCalendar(firstYear, firstMonth, size, []));
        resetMonths();
    };

    const renderTopBar = (): ReactNode => (
        <div className="grid u-mb16 u-hide-gt768">
            <div className="grid__unit u-size1of3">
                <FlatButton data-testid="reset-changing-departure" onClick={reset} small noBreak>
                    Nullstill
                </FlatButton>
            </div>
            <div className="grid__unit u-size2of3 u-text-right">
                <CallToActionButton data-testid="dismiss-changing-departure" onClick={close} small noBreak>
                    Ferdig
                </CallToActionButton>
            </div>
        </div>
    );

    const renderMonth = ({ year, month, selected }: CalendarMonth): ReactNode => {
        const buttonStyle = {
            paddingLeft: '2px !important',
            paddingRight: '2px !important',
        };

        if (selected) {
            return (
                <div key={`month-button-${year}-${month}-container`} className="grid__unit u-text-center u-ph2 u-mv2">
                    <CallToActionButton
                        data-testid={`month-button-${year}-${month}`}
                        onClick={(): void => monthClicked({ year, month, selected })}
                        className="u-size1of1 button--small--gt768"
                        style={buttonStyle}
                        noBreak>
                        {monthNames[month - 1].full}
                    </CallToActionButton>
                </div>
            );
        }
        return (
            <div key={`month-button-${year}-${month}-container`} className="grid__unit u-text-center u-ph2 u-mv2">
                <FlatButton
                    data-testid={`month-button-${year}-${month}`}
                    onClick={(): void => monthClicked({ year, month, selected })}
                    className="month-button u-size1of1 button--small--gt768"
                    style={buttonStyle}
                    noBreak>
                    {monthNames[month - 1].full}
                </FlatButton>
            </div>
        );
    };

    const renderMonths = (year: number): ReactNode => (
        <div key={`year-container-${year}`}>
            <h5 key={`year-title-${year}`} data-testid={`year-title-${year}`} className="u-sardine u-text-center">
                {year}
            </h5>
            <div key={`month-container-${year}`} className="grid grid--cols3upto990 grid--cols4from990 u-mb8">
                {Object.keys(calendar[year]).map((month) => renderMonth(calendar[year][Number.parseInt(month)]))}
            </div>
        </div>
    );

    return (
        <div>
            {renderTopBar()}
            <div className="grid">
                <h4 className="u-t5 grid__unit">{title}</h4>

                <LinkButton
                    data-testid="dismiss-changing-departure-cross"
                    className="u-hide-lt768 grid__unit u-float-right"
                    onClick={close}
                    aria-label="Lukk">
                    <i className="icon icon--cross" />
                </LinkButton>
            </div>
            {Object.keys(calendar).map((year) => renderMonths(Number.parseInt(year)))}
        </div>
    );
};
